import React, { useState, useEffect, useMemo } from "react";
import {
  Eye,
  FileText,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertTriangle,
  X,
  Save,
  ThumbsUp,
  ExternalLink,
  Calendar,
  User,
  Users,
} from "lucide-react";
import { getToken } from "../auth/authConfig";
import axios from "axios";

interface KTTask {
  CriticalProjectsInfo: string | null;
  Department: string;
  EmailDocs: any[];
  EmployeeName: string;
  EndDate: string | null;
  KT_Department: string | null;
  KT_Manager: string | null;
  KT_Status: string;
  KeyResponsibilities: string | null;
  KtSessionTime: string;
  LastDay: string;
  ProjectDescription: string | null;
  ProjectName: string | null;
  Role: string | null;
  RoleForKT: string;
  SharePointLinks: any[];
  StartDate: string | null;
  created_by: string;
  created_time: string;
  documents: Array<{
    id: number;
    name: string;
    path: string;
    review_comments: string | null;
    review_status: string;
    review_time: string | null;
    reviewed_by: string | null;
    upload_time: string;
    uploaded_by: string;
  }>;
  employee_email: string;
  employee_id: number;
  kt_doc_list: any[];
  manager_email: string;
}

interface Document {
  id: string;
  name: string;
  status: "submitted" | "approved" | "needs-revision" | "pending";
  type: "mandatory" | "employee-provided";
  uploadedBy: "employee";
  feedback?: string;
  path?: string;
  upload_time?: string;
  uploaded_by?: string;
  review_status?: string; // Add this to preserve original review_status
}

interface SharePointLink {
  name: string;
  url: string;
  description: string;
}

interface EmailDoc {
  subject: string;
  date: string;
  description: string;
  sender_recipient?: string;
}

const ManagerDashboard: React.FC = () => {
  const [selectedTask, setSelectedTask] = useState<KTTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [documentFeedback, setDocumentFeedback] = useState<{
    [key: string]: string;
  }>({});
  const [ktTasks, setKtTasks] = useState<KTTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Define helper functions before they are used
  const mapReviewStatusToDocumentStatus = (
    reviewStatus: string
  ): "pending" | "submitted" | "approved" | "needs-revision" => {
    switch (reviewStatus?.toLowerCase()) {
      case "approved":
        return "approved";
      case "needs revision":
      case "needs-revision":
        return "needs-revision";
      case "submitted":
        return "submitted";
      case "pending":
      default:
        return "pending";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "in-progress":
      case "in progress":
      case "submitted":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "needs-revision":
      case "pending-review":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "needs-revision":
      case "pending-review":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const calculateDaysRemaining = (lastDay: string) => {
    const today = new Date();
    const endDate = new Date(lastDay);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Parse SharePoint links and Email docs from the selected task
  const sharePointLinks = useMemo<SharePointLink[]>(() => {
    if (!selectedTask?.SharePointLinks) return [];
    try {
      return Array.isArray(selectedTask.SharePointLinks)
        ? selectedTask.SharePointLinks
        : JSON.parse(selectedTask.SharePointLinks);
    } catch {
      return [];
    }
  }, [selectedTask?.SharePointLinks]);

  const emailDocs = useMemo<EmailDoc[]>(() => {
    if (!selectedTask?.EmailDocs) return [];
    try {
      return Array.isArray(selectedTask.EmailDocs)
        ? selectedTask.EmailDocs
        : JSON.parse(selectedTask.EmailDocs);
    } catch {
      return [];
    }
  }, [selectedTask?.EmailDocs]);

  // Convert project documents to our Document format
  const documents = useMemo<Document[]>(() => {
    if (!selectedTask?.documents) return [];
    return selectedTask.documents.map((doc) => ({
      id: doc.id.toString(),
      name: doc.name,
      status: mapReviewStatusToDocumentStatus(doc.review_status),
      type: "mandatory" as const,
      uploadedBy: "employee" as const,
      feedback: doc.review_comments || undefined,
      path: doc.path,
      upload_time: doc.upload_time,
      uploaded_by: doc.uploaded_by,
      review_status: doc.review_status, // Preserve original review_status
    }));
  }, [selectedTask?.documents]);

  const [managerInputs, setManagerInputs] = useState({
    KeyResponsibilities: "",
    ProjectDescription: "",
    CriticalProjectsInfo: "",
  });

  const [EmployeeInputs, setEmployeeInputs] = useState({
    KeyResponsibilities: "",
    ProjectDescription: "",
    CriticalProjectsInfo: "",
  });

  console.log("ManagerDashboard documents", EmployeeInputs);

  const fetchKTTasks = async () => {
    try {
      setLoading(true);
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/manager`;
      const token = await getToken();
      const response = await fetch(baseurl, {
        headers: {
          "oidc-token": token,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch KT tasks");
      }

      const result = await response.json();
      const newKtTasks = result.data || [];
      setKtTasks(newKtTasks);

      // Update selectedTask if modal is open and task exists in new data
      if (selectedTask && showTaskModal) {
        const updatedTask = newKtTasks.find(
          (task: KTTask) => task.employee_id === selectedTask.employee_id
        );
        if (updatedTask) {
          setSelectedTask(updatedTask);
          // Update manager inputs with fresh data
          setEmployeeInputs({
            KeyResponsibilities: updatedTask.KeyResponsibilities || "",
            ProjectDescription: updatedTask.ProjectDescription || "",
            CriticalProjectsInfo: updatedTask.CriticalProjectsInfo || "",
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Fetch KT tasks from API
  useEffect(() => {
    fetchKTTasks();
  }, []);

  const handleViewTask = (task: KTTask) => {
    setSelectedTask(task);
    setShowTaskModal(true);

    setEmployeeInputs({
      KeyResponsibilities: task.KeyResponsibilities || "",
      ProjectDescription: task.ProjectDescription || "",
      CriticalProjectsInfo: task.CriticalProjectsInfo || "",
    });
    // Set manager inputs from task data
    // setManagerInputs({
    //   focusArea: task.KeyResponsibilities || "",
    //   jobDescription: task.ProjectDescription || "",
    //   projectDetails: task.CriticalProjectsInfo || "",
    // });
  };

  const handleFeedbackChange = (docId: string, feedback: string) => {
    setDocumentFeedback((prev) => ({ ...prev, [docId]: feedback }));
  };

  const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/review_document`;

  const handleApproveDocument = async (docId: string) => {
    const token = await getToken();
    const feedback = documentFeedback[docId];
    if (!feedback) {
      alert("Feedback required for approval.");
      return;
    }
    try {
      const response = await axios.post(
        baseurl,
        {
          document_id: docId,
          review_status: "approved",
          review_comments: feedback,
        },
        {
          headers: { "oidc-token": token },
        }
      );
      console.log("Document approved:", response.data);

      // Clear the feedback for this document after successful submission
      setDocumentFeedback((prev) => ({ ...prev, [docId]: "" }));

      // Refresh data
      await fetchKTTasks();
    } catch (error) {
      console.error("Failed to approve document:", error);
      alert("Failed to approve document. Please try again.");
    }
  };

  const handleRequestRevision = async (docId: string) => {
    const token = await getToken();
    const feedback = documentFeedback[docId];
    if (!feedback) {
      alert("Feedback required for revision request.");
      return;
    }
    try {
      const response = await axios.post(
        baseurl,
        {
          document_id: docId,
          review_status: "Need Revision",
          review_comments: feedback,
        },
        {
          headers: { "oidc-token": token },
        }
      );
      console.log("Revision requested:", response.data);

      // Clear the feedback for this document after successful submission
      setDocumentFeedback((prev) => ({ ...prev, [docId]: "" }));

      // Refresh data
      await fetchKTTasks();
    } catch (error) {
      console.error("Failed to request revision:", error);
      alert("Failed to request revision. Please try again.");
    }
  };

  const handleManagerInputChange = (field: string, value: string) => {
    setManagerInputs((prev) => ({ ...prev, [field]: value }));
  };

  const handleViewDocument = (doc: Document) => {
    if (doc.path) {
      const fileUrl = `/api/files/${doc.path}`;
      const viewableExtensions = [
        ".pdf",
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".txt",
        ".html",
      ];
      const fileExtension = doc.name
        .toLowerCase()
        .substring(doc.name.lastIndexOf("."));

      if (viewableExtensions.includes(fileExtension)) {
        window.open(fileUrl, "_blank", "noopener,noreferrer");
      } else {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      alert("Document path not available");
    }
  };

  const handleSubmit = async (doc: Document) => {
    const submissionData = {
      employee_id: selectedTask?.employee_id,
      KTFocusAreas: {
        KeyResponsibilities: managerInputs.KeyResponsibilities,
        ProjectDescription: managerInputs.ProjectDescription,
        CriticalProjectsInfo: managerInputs.CriticalProjectsInfo,

      },
    };

    console.log("Submitting data:", submissionData);

    // Example: API call
    try {
      const token = await getToken(); // if using OIDC token
      axios
        .post(
          `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/manager/submit_feedback`,
          submissionData,
          {
            headers: {
              "Content-Type": "application/json",
              "oidc-token": token,
            },
          }
        )
        .then((res) => {
          console.log("✔️ Submit success:", res.data);
          setShowTaskModal(false); // close modal
        })
        .catch((err) => {
          console.error("❌ Submit error:", err);
        });
    } catch (error) {
      console.error("❌ Submit error:", error);
    }
  };

  if (loading) {
    return (
      <div
        role="status"
        style={{
          position: "absolute",
          left: "0px",
          right: "0px",
          top: "0px",
          bottom: "0px",
          background: "rgba(0,0,0,0.8)",
          zIndex: "99999",
          display: "grid",
          alignContent: "center",
          justifyContent: "center",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="pt-4 text-white">
          Loading Manager, Dashboard Please wait...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 welcome-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 welcome-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Manager Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Review and approve knowledge transfer submissions from your team
        </p>
      </div>

      {/* KT Tasks Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Team KT Tasks</h2>
          <p className="text-gray-600 mt-1">
            Review and manage knowledge transfer tasks for your team members
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Day
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Remaining
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ktTasks &&
                ktTasks.length > 0 &&
                ktTasks.map((task) => {
                  const daysRemaining = calculateDaysRemaining(task.LastDay);
                  return (
                    <tr
                      key={task.employee_id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {task.EmployeeName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {task.Department}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {task.Role || task.RoleForKT}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatDate(task.LastDay)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`text-sm ${
                            daysRemaining <= 7
                              ? "text-red-600 font-semibold"
                              : "text-gray-600"
                          }`}
                        >
                          {daysRemaining} days
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            task.KT_Status
                          )}`}
                        >
                          {getStatusIcon(task.KT_Status)}
                          {task.KT_Status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewTask(task)}
                          disabled={task.KT_Status === "Completed"}
                          className={
                            "inline-flex items-center gap-2 px-3 py-1.5  text-sm font-medium rounded-lg transition-colors shadow-sm " +
                            (task.KT_Status === "Completed"
                              ? "cursor-not-allowed bg-green-200 text-green-900"
                              : "hover:bg-blue-700 bg-blue-600 text-white")
                          }
                        >
                          <Eye className="w-4 h-4" />
                          {task.KT_Status === "Completed"
                            ? "Reviewed"
                            : "Review Task"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              {ktTasks.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No employees found
                      </p>
                      <p className="text-gray-400">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task Modal */}
      {showTaskModal && selectedTask && (
        <div
          style={{ zIndex: "99999" }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Review KT - {selectedTask.EmployeeName}
                </h2>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>
                      KT Manager: {selectedTask.KT_Manager || "Not assigned"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Session: {formatDate(selectedTask.KtSessionTime)}
                    </span>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      selectedTask.KT_Status
                    )}`}
                  >
                    {selectedTask.KT_Status}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowTaskModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Project Overview */}
              {selectedTask.ProjectName && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {selectedTask.ProjectName}
                  </h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {selectedTask.ProjectDescription}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                    <span>Department: {selectedTask.Department}</span>
                    <span>•</span>
                    <span>KT Department: {selectedTask.KT_Department}</span>
                    {selectedTask.EndDate && (
                      <>
                        <span>•</span>
                        <span>
                          End Date: {formatDate(selectedTask.EndDate)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Document Review & Feedback */}
              {documents.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Document Review & Feedback
                  </h3>
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <span className="font-medium text-gray-900">
                                {doc.name}
                              </span>
                              <span className="font-medium text-gray-900 block text-sm">
                                {doc.review_status}
                              </span>
                              {doc.upload_time && (
                                <p className="text-xs text-gray-500">
                                  Uploaded: {formatDate(doc.upload_time)} by{" "}
                                  {doc.uploaded_by}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 transition-colors"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                doc.status
                              )}`}
                            >
                              {getStatusIcon(doc.status)}
                              {doc.status
                                .replace("-", " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                        </div>

                        {doc?.review_status === "Pending" && (
                          <div className="space-y-3">
                            <div className="flex items-start gap-3">
                              <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                              <textarea
                                placeholder="Add comments or feedback for the employee..."
                                rows={2}
                                value={documentFeedback[doc.id] || ""}
                                onChange={(e) =>
                                  handleFeedbackChange(doc.id, e.target.value)
                                }
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveDocument(doc.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <ThumbsUp className="w-3 h-3" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRequestRevision(doc.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                Request Revision
                              </button>
                            </div>
                          </div>
                        )}

                        {doc.feedback && (
                          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 text-yellow-600 mt-0.5" />
                              <div>
                                <p className="text-sm font-medium text-yellow-800">
                                  Your Feedback
                                </p>
                                <p className="text-sm text-yellow-700">
                                  {doc.feedback}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SharePoint Links */}
              {sharePointLinks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Important SharePoint Resources
                  </h3>
                  <div className="space-y-3">
                    {sharePointLinks.map((link, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            {link.name}
                          </span>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Open Link
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-sm text-gray-600">
                          {link.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          URL: {link.url}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Documents */}
              {emailDocs.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Important Email Communications
                  </h3>
                  <div className="space-y-3">
                    {emailDocs.map((email, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            {email.subject}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(email.date)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {email.description}
                        </p>
                        {email.sender_recipient && (
                          <p className="text-xs text-gray-500 mt-1">
                            From/To: {email.sender_recipient}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* KT Focus Areas */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Project Descriptions
                </h3>
                <div className="space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Key Responsibilities
                    </label>
                    <textarea
                      value={EmployeeInputs.KeyResponsibilities}
                      disabled={true}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe the key areas that should be the primary focus during knowledge transfer..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Description
                    </label>
                    <textarea
                      value={EmployeeInputs.ProjectDescription}
                      disabled={true}

                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Provide detailed job description and key responsibilities..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Critical Projects & Information
                    </label>
                    <textarea
                      value={EmployeeInputs.CriticalProjectsInfo}
                      disabled={true}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="List important ongoing projects, stakeholder relationships, and other critical details..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Current Role
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedTask.Role || "Not specified"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        KT Role
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedTask.RoleForKT}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  KT Focus Areas
                </h3>
                <div className="space-y-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Key Responsibilities
                    </label>
                    <textarea
                      value={managerInputs.KeyResponsibilities}
                      onChange={(e) =>
                        handleManagerInputChange("KeyResponsibilities", e.target.value)
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe the key areas that should be the primary focus during knowledge transfer..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Description
                    </label>
                    <textarea
                      value={managerInputs.ProjectDescription}
                      onChange={(e) =>
                        handleManagerInputChange(
                          "ProjectDescription",
                          e.target.value
                        )
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Provide detailed job description and key responsibilities..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Critical Projects & Information
                    </label>
                    <textarea
                      value={managerInputs.CriticalProjectsInfo}
                      onChange={(e) =>
                        handleManagerInputChange(
                          "CriticalProjectsInfo",
                          e.target.value
                        )
                      }
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="List important ongoing projects, stakeholder relationships, and other critical details..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Current Role
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedTask.Role || "Not specified"}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        KT Role
                      </label>
                      <div className="text-sm font-medium text-gray-900">
                        {selectedTask.RoleForKT}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleSubmit}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <Save className="w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;
